import { useState, useEffect } from 'react';
import { Bell, BellOff, BellRing, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = "/api";

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

const NotificationBell = () => {
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [supported, setSupported] = useState(false);

  useEffect(() => {
    const checkSupport = async () => {
      if ('serviceWorker' in navigator && 'PushManager' in window) {
        setSupported(true);
        try {
          const reg = await navigator.serviceWorker.ready;
          const sub = await reg.pushManager.getSubscription();
          setSubscribed(!!sub);
        } catch {
          // ignore
        }
      }
    };
    checkSupport();
  }, []);

  const handleToggle = async () => {
    if (!supported) {
      toast.error('Push notifications are not supported in this browser');
      return;
    }

    setLoading(true);
    try {
      if (subscribed) {
        // Unsubscribe
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          await axios.delete(`${API}/push/unsubscribe`, {
            data: { subscription: sub.toJSON() }
          });
          await sub.unsubscribe();
        }
        setSubscribed(false);
        toast.success('Notifications turned off');
      } else {
        // Subscribe
        const vapidRes = await axios.get(`${API}/push/vapid-key`);
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidRes.data.public_key)
        });
        await axios.post(`${API}/push/subscribe`, {
          subscription: sub.toJSON()
        });
        setSubscribed(true);
        toast.success('Notifications enabled! You\'ll get notified about new events and featured content.');
      }
    } catch (error) {
      console.error('Notification toggle failed:', error);
      if (Notification.permission === 'denied') {
        toast.error('Notifications are blocked. Enable them in your browser settings.');
      } else {
        toast.error('Failed to toggle notifications');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!supported) return null;

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={handleToggle}
      disabled={loading}
      data-testid="notification-bell"
      className="relative"
      title={subscribed ? 'Notifications on' : 'Enable notifications'}
    >
      {loading ? (
        <Loader2 size={18} className="animate-spin" />
      ) : subscribed ? (
        <BellRing size={18} className="text-primary" />
      ) : (
        <Bell size={18} className="text-muted-foreground" />
      )}
    </Button>
  );
};

export default NotificationBell;
